import { normalizeMappingStatus } from './constants';
import {
    getConfigureBusinessCatalogue,
    normalizeConfigureCatalogueResponse,
    isConfigureCatalogueSuccess,
    hasCatalogueBusinesses,
    getConfigureBusinessMappingsPage,
    normalizeConfigureMappingsPageResponse,
    isConfigureMappingsPageSuccess,
    mergeEntityListById,
    finalizeMappingsHierarchy
} from '../../../utils/configureMappingEventHandle';

const STORE_KEY = 'qualdo_configure_v19';
export const CONFIGURE_SCHEMA_VERSION = 19;

const EMPTY_STATE = {
    businesses: [],
    functions: [],
    kpis: [],
    models: [],
    assets: [],
    mappings: []
};

function ensureArray(value, fallback = []) {
    return Array.isArray(value) ? value : fallback;
}

export function getDefaultEnvironmentId(environments = []) {
    if (!Array.isArray(environments) || environments.length === 0) return null;
    const environment = environments[0];
    if (typeof environment === 'string' || typeof environment === 'number') {
        return environment;
    }
    return environment?.value ?? environment?.env_id ?? environment?.id ?? null;
}

function normalizeAsset(item = {}) {
    return {
        id: item.id ?? item.asset_id,
        datasource: item.datasource || item.data_source || item.datasource_name || '',
        datasource_id: item.datasource_id ?? item.datasourceId ?? null,
        dataset: item.dataset || item.dataset_name || item.table || '',
        attribute: item.attribute || item.attribute_name || item.column || '',
        env_id: item.env_id ?? item.envId ?? null,
        env_name: item.env_name || item.envName || '',
        pipeline_id: item.pipeline_id ?? item.pipelineId ?? null,
        pipeline_name: item.pipeline_name || item.pipelineName || '',
        pipeline_status: item.pipeline_status || item.pipelineStatus || '',
        pipelines: Array.isArray(item.pipelines) ? item.pipelines : undefined,
        metrics: Array.isArray(item.metrics) ? item.metrics : undefined,
        type: item.type || item.datasource_type || item.datasourceType || ''
    };
}

function normalizeMapping(item = {}) {
    return {
        id: item.id ?? item.mapping_id,
        kpiId: item.kpiId ?? item.kpi_id ?? item.kpi?.id ?? null,
        assetId: item.assetId ?? item.asset_id ?? item.asset?.id ?? null,
        modelId: item.modelId ?? item.model_id ?? item.model?.id ?? null,
        status: normalizeMappingStatus(item.status),
        enabled: item.enabled !== false && item.enable !== false
    };
}

function buildEmptyConfigureState() {
    return normalizeConfigureState(EMPTY_STATE);
}

export function normalizeConfigureState(raw = {}) {
    return {
        businesses: ensureArray(raw.businesses),
        functions: ensureArray(raw.functions),
        kpis: ensureArray(raw.kpis),
        models: ensureArray(raw.models),
        assets: ensureArray(raw.assets).map(normalizeAsset),
        mappings: ensureArray(raw.mappings).map(normalizeMapping),
        _schemaVersion: CONFIGURE_SCHEMA_VERSION
    };
}

function writeStore(state) {
    try {
        localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {
        console.error(e);
    }
    return state;
}

function emitStateUpdate(state) {
    window.dispatchEvent(new CustomEvent('qualdo-state-update', { detail: state }));
}

export function loadConfigureState() {
    try {
        const raw = localStorage.getItem(STORE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed._schemaVersion !== CONFIGURE_SCHEMA_VERSION) {
                return writeStore(buildEmptyConfigureState());
            }
            return normalizeConfigureState(parsed);
        }
    } catch (e) {
        console.error('Error loading config state from localStorage:', e);
    }

    return writeStore(buildEmptyConfigureState());
}

export function mergeConfigureState(current, partial = {}) {
    const base = normalizeConfigureState(current || EMPTY_STATE);
    const next = { ...base };

    Object.keys(EMPTY_STATE).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(partial, key)) {
            next[key] = ensureArray(partial[key]);
        }
    });

    next._schemaVersion = CONFIGURE_SCHEMA_VERSION;
    return next;
}

export function saveConfigureState(state) {
    try {
        const normalized = normalizeConfigureState(state);
        writeStore(normalized);
        emitStateUpdate(normalized);
        return normalized;
    } catch (e) {
        console.error('Error saving config state from localStorage:', e);
        return normalizeConfigureState(state);
    }
}

/** Slice entity arrays for React setState after persist. */
export function getEntitySlice(state = {}) {
    const normalized = normalizeConfigureState(state);
    return {
        businesses: normalized.businesses,
        functions: normalized.functions,
        kpis: normalized.kpis,
        models: normalized.models,
        assets: normalized.assets,
        mappings: normalized.mappings
    };
}

let _catalogueHydratePromise = null;
let _mappingsHydratePromise = null;
let _mappingsHydrateEnvId = null;
let _lastHydratedCatalogueState = null;

/**
 * Fetch KPI catalogue from GET configure/business/catalogue and merge into store.
 * Keeps existing assets/mappings from prior state.
 */
export function hydrateConfigureCatalogueFromApi() {
    if (_catalogueHydratePromise) {
        return _catalogueHydratePromise;
    }

    _catalogueHydratePromise = (async () => {
        try {
            const response = await getConfigureBusinessCatalogue({
                tenant_id: localStorage.getItem('team_id'),
                user_id: localStorage.getItem('user_id')
            });

            if (!isConfigureCatalogueSuccess(response) && !hasCatalogueBusinesses(response)) {
                return {
                    state: _lastHydratedCatalogueState || loadConfigureState(),
                    fromApi: false,
                    message: response?.message || 'Failed to load catalogue'
                };
            }

            const catalogue = normalizeConfigureCatalogueResponse(response);
            if (!catalogue.businesses.length) {
                return {
                    state: _lastHydratedCatalogueState || loadConfigureState(),
                    fromApi: false,
                    message: response?.message || 'Catalogue returned no businesses'
                };
            }

            const prior = _lastHydratedCatalogueState || loadConfigureState();
            const next = mergeConfigureState(prior, {
                businesses: catalogue.businesses,
                functions: catalogue.functions,
                kpis: catalogue.kpis,
                models: catalogue.models
            });
            saveConfigureState(next);
            _lastHydratedCatalogueState = next;

            return { state: next, fromApi: true, message: response?.message };
        } catch (e) {
            console.error('Error hydrating configure catalogue from API:', e);
            return {
                state: _lastHydratedCatalogueState || loadConfigureState(),
                fromApi: false,
                message: 'Failed to load catalogue'
            };
        } finally {
            _catalogueHydratePromise = null;
        }
    })();

    return _catalogueHydratePromise;
}

/**
 * Fetch Data Assets Mapping page from GET configure/business/mappings/page.
 * Returns full page state for DomainMappingView (hierarchy + assets + mappings).
 * Hierarchy is persisted so mapping joins survive reloads and qualdo-state-update.
 */
export function hydrateConfigureMappingsFromApi(envId = null) {
    const requestEnvId = envId !== null && envId !== undefined && String(envId) !== ''
        ? String(envId)
        : null;
    if (_mappingsHydratePromise && _mappingsHydrateEnvId === requestEnvId) {
        return _mappingsHydratePromise;
    }

    const hydratePromise = (async () => {
        try {
            const requestParams = {
                tenant_id: localStorage.getItem('team_id'),
                user_id: localStorage.getItem('user_id')
            };
            if (requestEnvId !== null) {
                requestParams.env_id = requestEnvId;
            }

            const response = await getConfigureBusinessMappingsPage(requestParams);

            if (!isConfigureMappingsPageSuccess(response)) {
                return {
                    state: loadConfigureState(),
                    fromApi: false,
                    metrics: null,
                    message: response?.message || 'Failed to load mappings page'
                };
            }

            const page = normalizeConfigureMappingsPageResponse(response);
            const prior = loadConfigureState();

            let mergedBusinesses = mergeEntityListById(prior.businesses, page.businesses);
            let mergedFunctions = mergeEntityListById(prior.functions, page.functions);
            let mergedKpis = mergeEntityListById(prior.kpis, page.kpis);
            let mergedModels = mergeEntityListById(prior.models, page.models);

            const expectedTotal = Math.max(
                page.metrics?.totalActiveKpis || 0,
                (page.metrics?.mappedCount || 0) + (page.metrics?.unmappedCount || 0)
            );
            const activeKpiCount = mergedKpis.filter((k) => k.active !== false).length;

            if (expectedTotal > activeKpiCount) {
                try {
                    const catResponse = await getConfigureBusinessCatalogue({
                        tenant_id: localStorage.getItem('team_id'),
                        user_id: localStorage.getItem('user_id')
                    });
                    if (isConfigureCatalogueSuccess(catResponse) || hasCatalogueBusinesses(catResponse)) {
                        const catalogue = normalizeConfigureCatalogueResponse(catResponse);
                        // Keep the environment-scoped mappings/page fields authoritative.
                        // The catalogue can report a parent as inactive even though the page
                        // contains active mapped KPIs beneath it; letting catalogue data win
                        // makes the table's effective-active filters remove every row.
                        mergedBusinesses = mergeEntityListById(catalogue.businesses, mergedBusinesses);
                        mergedFunctions = mergeEntityListById(catalogue.functions, mergedFunctions);
                        mergedKpis = mergeEntityListById(catalogue.kpis, mergedKpis);
                        mergedModels = mergeEntityListById(catalogue.models, mergedModels);
                    }
                } catch (catErr) {
                    console.warn('Could not merge catalogue while hydrating mappings page:', catErr);
                }
            }

            const hierarchy = finalizeMappingsHierarchy(
                mergedBusinesses,
                mergedFunctions,
                mergedKpis
            );

            const hasHierarchy = (
                hierarchy.businesses.length > 0
                || hierarchy.functions.length > 0
                || mergedKpis.length > 0
            );

            const viewState = mergeConfigureState(prior, {
                assets: page.assets,
                mappings: page.mappings,
                ...(hasHierarchy
                    ? {
                        businesses: hierarchy.businesses,
                        functions: hierarchy.functions,
                        kpis: mergedKpis,
                        models: mergedModels
                    }
                    : {})
            });

            saveConfigureState(viewState);

            return {
                state: viewState,
                fromApi: true,
                metrics: page.metrics,
                filter_options: page.filter_options,
                message: response?.message
            };
        } catch (e) {
            console.error('Error hydrating configure mappings from API:', e);
            return {
                state: loadConfigureState(),
                fromApi: false,
                metrics: null,
                message: 'Failed to load mappings page'
            };
        } finally {
            if (_mappingsHydratePromise === hydratePromise) {
                _mappingsHydratePromise = null;
                _mappingsHydrateEnvId = null;
            }
        }
    })();

    _mappingsHydratePromise = hydratePromise;
    _mappingsHydrateEnvId = requestEnvId;
    return _mappingsHydratePromise;
}
